import { useState, useEffect } from 'react'
import { Separator, Table, Button, Text, Card } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { DateTime } from 'luxon'
import { adminGroupUserTaxEstimates } from '@/features/taxes/quarterly/userTaxEstimates.selector'

import { useAppDispatch } from '@/utils/typeHelpers'
import {
  fetchSingleUserTaxEstimates,
  UserTaxEstimate,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { getFinancialProfileByUserId } from '@/selectors/user.selectors'
import { centsToDollars } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'
import { UserTaxEstimateUpdateDialog } from './UserTaxEstimateUpdateDialog'
import { AddStateEstimateDialog } from './AddStateEstimateDialog'
import { QuarterlyTaxEstimateCoverageAlert } from './QuarterlyTaxEstimateCoverageAlert'
/*
  Handles ALL UserTaxEstimates by quarter
*/

const TaxEstimateValueCellGroup = ({
  estimate,
}: {
  estimate: UserTaxEstimate
}) => {
  if (estimate.noTaxPaymentNeeded) {
    /* If no tax payment needed, do not show to user*/
    return (
      <Table.Cell colSpan={2}>No payment needed — Tax estimate of 0</Table.Cell>
    )
  } else {
    return (
      <>
        <Table.Cell>
          {!estimate.estimateInCents ? (
            'No Estimate Yet'
          ) : (
            <CurrencyFormatLabel
              value={centsToDollars(estimate.estimateInCents)}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          {!estimate.minimumPaymentInCents ? (
            'N/A'
          ) : (
            <CurrencyFormatLabel
              value={centsToDollars(estimate.minimumPaymentInCents)}
            />
          )}
        </Table.Cell>

        <Table.Cell>
          {!estimate.amountPaidInCents ? (
            'N/A'
          ) : (
            <CurrencyFormatLabel
              value={centsToDollars(estimate.amountPaidInCents)}
            />
          )}
        </Table.Cell>
      </>
    )
  }
}

const TaxEstimateRow = ({
  estimate,
  showEstimate,
}: {
  estimate: UserTaxEstimate
  showEstimate: boolean
}) => {
  return (
    <Table.Row
      key={estimate.id}
      className={
        !estimate.paidAt && showEstimate && !estimate.noTaxPaymentNeeded
          ? 'bg-red-25'
          : ''
      }
    >
      <Table.Cell>{estimate.id}</Table.Cell>
      <Table.Cell>{estimate.type}</Table.Cell>
      <Table.Cell>{estimate.filingState || 'N/A'}</Table.Cell>
      <TaxEstimateValueCellGroup estimate={estimate} />

      <Table.Cell className={!estimate.paidAt ? 'text-red-500' : ''}>
        {estimate.paidAt
          ? DateTime.fromISO(estimate.paidAt).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_SHORT
            )
          : 'Unpaid'}
      </Table.Cell>
      <Table.Cell>
        {estimate.notifiedAt
          ? DateTime.fromISO(estimate.notifiedAt).toFormat(
              DATE_FORMATS_LUXON.DISPLAY_SHORT
            )
          : 'N/A'}
      </Table.Cell>
      <Table.Cell>{estimate.status}</Table.Cell>
    </Table.Row>
  )
}

/*
  Used to denote tax estimates by Quarter
*/
const TaxEstimateQuarter = ({
  estimates,
  firstEstimate,
  userId,
  homeState,
}: {
  estimates: UserTaxEstimate[]
  firstEstimate: UserTaxEstimate
  userId: number
  homeState?: string | null
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [taxYear, taxQuarterNum] = firstEstimate.taxQuarter.split('-')
  return (
    <>
      <Table.Row className={!firstEstimate.paidAt ? 'bg-red-25' : ''}>
        <Table.Cell rowSpan={estimates.length}>
          Q {`${taxQuarterNum}, ${taxYear}`}
        </Table.Cell>
        <Table.Cell>{firstEstimate.id}</Table.Cell>
        <Table.Cell>{firstEstimate.type}</Table.Cell>
        <Table.Cell>{firstEstimate.filingState || 'N/A'}</Table.Cell>
        <Table.Cell>
          {!firstEstimate.estimateInCents ? (
            'No Estimate Yet'
          ) : (
            <CurrencyFormatLabel
              value={centsToDollars(firstEstimate.estimateInCents)}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          {!firstEstimate.minimumPaymentInCents ? (
            'N/A'
          ) : (
            <CurrencyFormatLabel
              value={centsToDollars(firstEstimate.minimumPaymentInCents)}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          {!firstEstimate.amountPaidInCents ? (
            'N/A'
          ) : (
            <CurrencyFormatLabel
              value={centsToDollars(firstEstimate.amountPaidInCents)}
            />
          )}
        </Table.Cell>
        <Table.Cell className={!firstEstimate.paidAt ? 'bg-red-25' : ''}>
          {firstEstimate.paidAt
            ? DateTime.fromISO(firstEstimate.paidAt).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              )
            : 'Unpaid'}
        </Table.Cell>
        <Table.Cell>
          {firstEstimate.notifiedAt
            ? DateTime.fromISO(firstEstimate.notifiedAt).toFormat(
                DATE_FORMATS_LUXON.DISPLAY_SHORT
              )
            : 'N/A'}
        </Table.Cell>
        <Table.Cell>{firstEstimate.status}</Table.Cell>
        <Table.Cell rowSpan={estimates.length}>
          <Button className="link" onClick={() => setModalOpen(true)}>
            Update
          </Button>
        </Table.Cell>
        {modalOpen && (
          <UserTaxEstimateUpdateDialog
            userId={userId}
            open={modalOpen}
            close={() => setModalOpen(false)}
            estimates={estimates}
            taxQuarter={firstEstimate.taxQuarter}
            homeState={homeState}
          />
        )}
      </Table.Row>
      {estimates.slice(1).map((estimate) => {
        const showEstimate: boolean =
          estimate.filingState === homeState || !homeState

        return (
          <TaxEstimateRow
            estimate={estimate}
            key={estimate.id}
            showEstimate={showEstimate}
          />
        )
      })}
    </>
  )
}

interface TaxTabPanelProps {
  userId: number
}
export const TaxTabPanel = ({ userId }: TaxTabPanelProps) => {
  const dispatch = useAppDispatch()
  const [addStateOpen, setAddStateOpen] = useState<boolean>(false)
  const userTaxEstimates = useReselector(adminGroupUserTaxEstimates, userId)
  const financialProfile = useReselector(getFinancialProfileByUserId, userId)

  useEffect(() => {
    fetchSingleUserTaxEstimates(userId)(dispatch)
  }, [userId, dispatch])

  const renderEstimates = () => {
    if (Object.keys(userTaxEstimates).length === 0) {
      return (
        <Text variant={TypographyStyles.H3}>No Quarterly Tax estimates</Text>
      )
    }

    return (
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Tax Quarter</Table.HeaderCell>
            <Table.HeaderCell>Id</Table.HeaderCell>
            <Table.HeaderCell>Type</Table.HeaderCell>
            <Table.HeaderCell>Filing State</Table.HeaderCell>
            <Table.HeaderCell>Estimate</Table.HeaderCell>
            <Table.HeaderCell>Minimum Estimate</Table.HeaderCell>
            <Table.HeaderCell>Amount Paid</Table.HeaderCell>
            <Table.HeaderCell>Paid At</Table.HeaderCell>
            <Table.HeaderCell>Notified At</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Action</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {financialProfile &&
            Object.keys(userTaxEstimates).map((taxQuarter) => {
              const estimates: UserTaxEstimate[] = userTaxEstimates[taxQuarter]
              const firstEstimate: UserTaxEstimate = estimates[0]
              return (
                <TaxEstimateQuarter
                  key={taxQuarter}
                  userId={userId}
                  estimates={estimates}
                  firstEstimate={firstEstimate}
                  homeState={financialProfile.homeState}
                />
              )
            })}
        </Table.Body>
      </Table>
    )
  }

  const taxQuarters = Object.keys(userTaxEstimates)

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <Text variant={TypographyStyles.H2}>Quarterly Tax Estimates</Text>
        <Button
          variant="secondary"
          disabled={taxQuarters.length === 0}
          onClick={() => setAddStateOpen(true)}
        >
          Add state record
        </Button>
      </div>
      <Separator />
      <div className="my-4">
        <QuarterlyTaxEstimateCoverageAlert userId={userId} />
      </div>
      {renderEstimates()}
      {addStateOpen && (
        <AddStateEstimateDialog
          open={addStateOpen}
          close={() => setAddStateOpen(false)}
          userId={userId}
          taxQuarters={taxQuarters}
        />
      )}
    </Card>
  )
}
